"use client";

// "Book a call" form of the homepage (v2). Rendered inside components/home-v2/contact.tsx.
// Behaviour kept from the previous form: validation on submit (the role is optional), an error clears as its field is
// typed in, POST JSON to /api/lead with the anti-bot fields (_gotcha honeypot, _elapsed since mount), one request at a
// time, success replaces the form with a role="status" block, and a failed or thrown request opens the mail client with
// a pre-filled message.
// Added: aria-invalid + aria-describedby on the fields in error, focus on the first one, focus on the success block.
// While sending, the button is aria-disabled (not disabled, which would drop the keyboard focus onto <body>).

import { useEffect, useRef, useState } from "react";
import Button from "@/components/ds/button";
import Field from "@/components/ds/field";

export type LeadFields = {
  name: string;
  firm: string;
  email: string;
  role: string;
  message: string;
  source: string;
  /** Free text when source is "Other". */
  sourceOther: string;
  _gotcha: string;
};

export type LeadErrors = {
  name: boolean;
  firm: boolean;
  email: boolean;
  message: boolean;
  source: boolean;
};

/** "How did you hear about us?" — fixed options so the answers can be counted. "Other" opens a free-text box. */
export const LEAD_SOURCES = [
  "Google search",
  "ChatGPT, Claude or another AI",
  "LinkedIn post",
  "Referral from a colleague",
  "Event or conference",
  "Newsletter or press",
  "Other",
] as const;

/** The value stored and emailed: the option, or "Other: <what they typed>". */
export function leadSource(fields: Pick<LeadFields, "source" | "sourceOther">) {
  const other = fields.sourceOther.trim();
  return fields.source === "Other" && other ? `Other: ${other}` : fields.source;
}

/** Required fields in page order: the first one in error takes the focus. */
const REQUIRED = ["name", "firm", "email", "source", "message"] as const;

/** id of each required field's control (the message box keeps its old id, "msg"). */
const CONTROL_ID: Record<keyof LeadErrors, string> = {
  name: "name",
  firm: "firm",
  email: "email",
  source: "source",
  message: "msg",
};

function validEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** Pure validation: true marks a field in error. */
export function validateLead(fields: Pick<LeadFields, keyof LeadErrors>): LeadErrors {
  return {
    name: fields.name.trim() === "",
    firm: fields.firm.trim() === "",
    email: !validEmail(fields.email.trim()),
    message: fields.message.trim() === "",
    source: fields.source === "",
  };
}

/** Fallback when the request fails: a pre-filled message to North. */
export function leadMailto(fields: LeadFields) {
  return (
    "mailto:ilan@northos.com?subject=" +
    encodeURIComponent("Call request, " + (fields.firm || "")) +
    "&body=" +
    encodeURIComponent(
      "Name: " +
        fields.name +
        "\nFirm: " +
        fields.firm +
        "\nEmail: " +
        fields.email +
        "\nRole: " +
        fields.role +
        "\nHeard about us: " +
        leadSource(fields) +
        "\n\n" +
        fields.message
    )
  );
}

const NO_ERRORS: LeadErrors = { name: false, firm: false, email: false, message: false, source: false };

export default function LeadForm() {
  const [fields, setFields] = useState<LeadFields>({
    name: "",
    firm: "",
    email: "",
    role: "",
    message: "",
    source: "",
    sourceOther: "",
    _gotcha: "",
  });
  const [errors, setErrors] = useState<LeadErrors>(NO_ERRORS);
  // The field to focus after a failed submit; a new object each time, so the same field is focused again.
  const [focusTarget, setFocusTarget] = useState<{ id: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Read synchronously by the handler: a second click or Enter while sending is ignored, whatever React has rendered.
  const sending = useRef(false);
  const [success, setSuccess] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);
  const mountedAt = useRef<number>(Date.now()); // anti-bot timing trap

  // After React has rendered the messages, so the field is announced with its description.
  useEffect(() => {
    if (focusTarget) document.getElementById(focusTarget.id)?.focus();
  }, [focusTarget]);

  useEffect(() => {
    const block = successRef.current;
    if (!success || !block) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    block.focus({ preventScroll: true });
    block.scrollIntoView({ behavior: reduce ? "instant" : "smooth", block: "center" });
  }, [success]);

  function update(field: keyof LeadFields, value: string) {
    setFields((prev) => ({ ...prev, [field]: value }));
    if (field in NO_ERRORS) setErrors((prev) => ({ ...prev, [field]: false }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending.current) return;
    const next = validateLead(fields);
    setErrors(next);
    const first = REQUIRED.find((key) => next[key]);
    if (first) {
      setFocusTarget({ id: CONTROL_ID[first] });
      return;
    }
    sending.current = true;
    setSubmitting(true);
    try {
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: fields.name,
          firm: fields.firm,
          email: fields.email,
          role: fields.role,
          message: fields.message,
          source: leadSource(fields),
          _gotcha: fields._gotcha,
          _elapsed: Date.now() - mountedAt.current,
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        window.location.href = leadMailto(fields);
      }
    } catch {
      window.location.href = leadMailto(fields);
    } finally {
      sending.current = false;
      setSubmitting(false);
    }
  }

  return (
    // Form and success block share one grid cell: the card keeps its height while one fades into the other.
    <div className="v2-lead" data-state={success ? "sent" : "open"}>
      <form
        id="leadForm"
        className="v2-lead__form"
        action="/api/lead"
        method="POST"
        noValidate
        onSubmit={handleSubmit}
        inert={success}
      >
        <div className="v2-lead__grid">
          <Field
            id="name"
            name="name"
            type="text"
            label="Name"
            placeholder="Jane Counsel"
            required
            autoComplete="name"
            value={fields.name}
            onChange={(e) => update("name", e.target.value)}
            invalid={errors.name}
            error="Please enter your name."
          />
          <Field
            id="firm"
            name="firm"
            type="text"
            label="Firm"
            placeholder="Firm name"
            required
            autoComplete="organization"
            value={fields.firm}
            onChange={(e) => update("firm", e.target.value)}
            invalid={errors.firm}
            error="Please enter your firm."
          />
          <Field
            id="email"
            name="email"
            type="email"
            label="Work email"
            placeholder="you@firm.com"
            required
            autoComplete="email"
            value={fields.email}
            onChange={(e) => update("email", e.target.value)}
            invalid={errors.email}
            error="Please enter a valid work email."
          />
          <Field
            control="select"
            id="role"
            name="role"
            label="Your role"
            value={fields.role}
            onChange={(e) => update("role", e.target.value)}
          >
            <option value="">Select</option>
            <option>Managing or Named Partner</option>
            <option>Partner</option>
            <option>Associate</option>
            <option>COO or Operations</option>
            <option>IT or Innovation</option>
            <option>Other</option>
          </Field>
          <Field
            control="select"
            id="source"
            name="source"
            label="How did you hear about us?"
            required
            value={fields.source}
            onChange={(e) => update("source", e.target.value)}
            invalid={errors.source}
            error="Please pick one."
          >
            <option value="">Select</option>
            {LEAD_SOURCES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Field>
          {fields.source === "Other" && (
            <Field
              id="sourceOther"
              name="sourceOther"
              type="text"
              label="Where, exactly?"
              placeholder="A podcast, a Slack group…"
              value={fields.sourceOther}
              onChange={(e) => update("sourceOther", e.target.value)}
            />
          )}
          <Field
            control="textarea"
            className="v2-lead__wide"
            id="msg"
            name="message"
            label="Where do the hours go?"
            placeholder="The work that eats your team's time: closings, drafting, intake, deadlines."
            required
            value={fields.message}
            onChange={(e) => update("message", e.target.value)}
            invalid={errors.message}
            error="Tell us a little about the work."
          />
        </div>
        <input
          type="text"
          name="_gotcha"
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={fields._gotcha}
          onChange={(e) => update("_gotcha", e.target.value)}
        />
        <div className="v2-lead__actions">
          <span className="v2-lead__submit">
            <Button
              variant="dark"
              icon="arrow"
              type="submit"
              aria-disabled={submitting || undefined}
              aria-busy={submitting || undefined}
            >
              Request a call
            </Button>
            {/* Sending: a thin light runs under the label (contact.css), no extra word. */}
            <span className="v2-lead__progress" aria-hidden="true" />
          </span>
          <p className="v2-lead__promise">
            We reply within one business day. Your details stay between us. Never sold, never
            spammed.
          </p>
        </div>
      </form>
      <div ref={successRef} className="v2-lead__success" id="formSuccess" role="status" aria-live="polite" tabIndex={-1}>
        <div className="v2-lead__success-body" hidden={!success}>
          <svg className="v2-lead__check" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <circle cx="20" cy="20" r="19" pathLength={1} />
            <path d="M12.5 20.5l5 5 10-11" pathLength={1} />
          </svg>
          <p className="v2-lead__success-title">Request received.</p>
          <p className="v2-lead__success-text">We will be in touch within one business day to schedule your call.</p>
        </div>
      </div>
    </div>
  );
}
